import { inject, Injectable } from '@angular/core';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { Model } from '@df/catalog/model';
import { IProductDataV2, IProductDataV3, Product as DfProduct } from '@df/catalog/product/product';
import { StructuralData } from '@df/core/meta/structural-data/structural-data.namespace';
import { StructuralDataService } from '@df/core/meta/structural-data/structural-data.service';
import { ICmsBlock } from '@icms/core/resource/block/icms-block';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { NICmsOutletBlockComponent } from '../catalog/outlet-block/icms-outlet-block.component';
import { Product } from '../catalog/product/product.decorator';
import { NCmsInformationFaq } from '../cms/information/components/faq/cms-information-faq.component';

import { ICategoryDataV3 } from '@df/catalog/category/category';
import { NICmsContentComponent } from '@icms/core/content';
import flatten from 'lodash-es/flatten';
import { Category } from '../catalog/category/category.decorator';
import { NCategoryViewLayout } from '../catalog/category/view/layout/category-view-layout.component';
import { NProductViewLayout } from '../catalog/product/view/layout/product-view-layout.namespace';
import { NCmsFaqComponent } from '../cms/faq/cms-faq.component';
import { NCmsText } from '../cms/text/cms-text.component';
import { dedupeReviews, TrustpilotReviewService } from './trustpilot-review.service';

interface IFaqItem {
    question: string;
    answer: string;
}

interface IExternalProductReviews {
    review?: StructuralData.IReviewData | StructuralData.IReviewData[];
    aggregateRating?: StructuralData.IAggregateRatingData;
}

@Injectable({
    providedIn: 'root'
})
export class StructuralDataServiceDecorator extends StructuralDataService {
    private iCmsBlockResource = inject(ICmsBlockResource);
    private trustpilotReviewService = inject(TrustpilotReviewService);

    /** Externally-merged reviews (e.g. Trustpilot) re-asserted on every updateScripts(). */
    private externalProductReviews?: IExternalProductReviews;
    /**
     * Product family (parent) id the external reviews belong to. Trustpilot reviews
     * are family-level, so they stay valid while browsing variants of the same
     * configurable (e.g. selecting a simple product) and are only reset when
     * navigating to a different product family.
     */
    private externalReviewsFamilyId?: string | number;

    /**
     * Whether the given node is an ld+json script this service placed itself
     * (as opposed to one injected by a third party such as Trustpilot).
     */
    isManagedLdJsonScript(node: Node): boolean {
        if (this._placedElements.some(el => el.script === node)) {
            return true;
        }
        // All scripts this service renders carry a `df-ld-` id (see _ldJsonScriptId),
        // so treat any such script as ours regardless of placement timing.
        return node instanceof Element && node.id.startsWith('df-ld-');
    }

    /**
     * Merges externally-sourced product reviews (e.g. from Trustpilot) into the
     * current Product/ProductGroup schema and re-renders it.
     * Returns whether a product element was present — i.e. whether the source
     * (duplicate) Trustpilot script can safely be removed. This is true even
     * when `data` carries no reviews: the duplicate Product block must still be
     * removed so Google sees a single Product schema.
     */
    applyExternalProductReviews(data: IExternalProductReviews): boolean {
        const product = this.findPrimaryProductElement();

        if (!product) {
            return false;
        }

        // Nothing to merge for a review-less Trustpilot Product block; signal the
        // caller to remove the duplicate without touching our rendered schema.
        if (!data.review && !data.aggregateRating) {
            return true;
        }

        // Remember the merged data so a later schema rebuild (which replaces the
        // in-memory product element) cannot drop it — see updateScripts(). Under
        // SSR/hydration the base service intermittently rebuilds the product schema
        // after this merge, and the source Trustpilot script is already removed, so
        // without re-asserting here the reviews would be lost until (and unless)
        // Trustpilot happens to re-inject — the intermittent disappearance.
        this.externalProductReviews = data;
        this.externalReviewsFamilyId = this.currentProductFamilyId();
        this.assignExternalReviews(product, data);

        this.updateScripts();
        return true;
    }

    /**
     * Re-asserts the externally-merged product reviews onto the current product
     * element before each serialisation, so a schema rebuild can't silently drop
     * them. Kept across variants of the same configurable (family-level reviews);
     * reset only when navigating to a different product family.
     */
    protected override updateScripts(): void {
        this.pruneHollowListing();

        if (this.externalProductReviews) {
            if (this.currentProductFamilyId() !== this.externalReviewsFamilyId) {
                this.externalProductReviews = undefined;
                this.externalReviewsFamilyId = undefined;
            } else {
                const product = this.findPrimaryProductElement();
                if (product) {
                    this.assignExternalReviews(product, this.externalProductReviews);
                }
            }
        }
        super.updateScripts();
    }

    /**
     * Drops a listing (category) ItemList element that carries no entries — an
     * empty ItemList is worse than no ItemList: it asserts to Google that the
     * page lists nothing, whereas omitting the node entirely just means "no
     * opinion". This CANNOT live in getListingData() instead:
     *  - its return type is non-optional, so it must hand back some node; and
     *  - even if it returned undefined, the base's setListingTracking() does
     *    `this.listingElement = this.add(this.getListingData(...))` — the
     *    assignment only happens AFTER add() (and the updateScripts() call
     *    inside it) has already run, so a stale/hollow listingElement would
     *    still be serialised for that pass regardless of what getListingData()
     *    returns;
     *  - and separately, clear() unconditionally does
     *    `if (this.listingElement) this._currentElements.push(this.listingElement)`
     *    on every model change, so a hollow node removed once would simply be
     *    re-pushed back in on the next render.
     * updateScripts() is the single choke point every serialisation (add(),
     * clear(), and direct calls) passes through, so pruning here — on every
     * pass — is the only place the fix actually sticks. It must therefore be
     * idempotent (it re-fires on every pass, so repeating it has to be
     * harmless) and must NOT call updateScripts() itself (recursion).
     */
    private pruneHollowListing(): void {
        const isHollowListing = (el: StructuralData.IData): boolean =>
            el['@type'] === StructuralData.Type.listing && !(el as StructuralData.IListingData).itemListElement?.length;

        if (this.listingElement && isHollowListing(this.listingElement)) {
            this.listingElement = undefined;
        }

        const hollow = this._currentElements.filter(isHollowListing);
        if (!hollow.length) {
            return;
        }

        hollow.forEach(el => this.removePlacedScript(el));
        this._currentElements = this._currentElements.filter(el => !isHollowListing(el));
    }

    /**
     * Removes an already-rendered ld+json script for an element we are about to
     * drop, so it cannot be orphaned in the DOM.
     *
     * The base's cleanup loop cannot do this for us across the SSR→client
     * boundary: `_placedElements` is per service instance and starts empty on
     * client bootstrap, so a script rendered by the *server* is unknown to the
     * browser's instance and is only ever reclaimed because HeadService.add()
     * reuses an existing element by id. Skipping the element (which is the whole
     * point of the prune) skips that reuse — so if the server rendered a
     * populated ItemList and the client's first pass comes out hollow (any
     * SSR↔hydration drift in price/rating data), the server's script would sit
     * in the DOM unmanaged forever.
     *
     * The id lines up because _ldJsonScriptId() keys a listing off its `url`
     * alone, not its contents, and the hollow node still carries the same url —
     * so it resolves to exactly the id the server used for the populated node.
     * If that keying ever changes to hash the full JSON, this lookup silently
     * stops matching and the orphan comes back.
     *
     * Only ever removes a script this service placed: the id comes from our own
     * _ldJsonScriptId(), which always produces a `df-ld-` key (the same marker
     * isManagedLdJsonScript() trusts), so the lookup cannot match a third-party
     * injection such as Trustpilot's Product block.
     */
    private removePlacedScript(element: StructuralData.IData): void {
        const id = this._ldJsonScriptId(JSON.stringify(element));
        const script = this.headService.findElement<HTMLScriptElement>(`#${id}`);
        if (script) {
            // Matches the base's own removal idiom — a script already detached by
            // another pass must not break this one.
            try {
                this.headService.remove(script);
            } catch {}
        }
    }

    private findPrimaryProductElement(): StructuralData.IProductDetailsData | undefined {
        return this._currentElements.find(
            el => el['@type'] === StructuralData.Type.product || el['@type'] === StructuralData.Type.productGroup
        ) as StructuralData.IProductDetailsData | undefined;
    }

    /**
     * The id of the current product's family (its parent for a variant/simple,
     * or itself for a configurable/standalone) — a stable key across variant
     * selection within one configurable product.
     */
    private currentProductFamilyId(): string | number | undefined {
        const model = this.meta.model;
        if (!model || model.type !== 'product') {
            return undefined;
        }
        const product = model as Product<IProductDataV3 | IProductDataV2>;
        return product.parent?.id ?? product.id;
    }

    private assignExternalReviews(product: StructuralData.IProductDetailsData, data: IExternalProductReviews): void {
        if (data.aggregateRating) {
            product.aggregateRating = data.aggregateRating;
        }
        if (data.review) {
            // schema.org permits a `review` array; the shared type models it as singular.
            // Dedupe first — Trustpilot's feed repeats some reviews (e.g. "Peter Brown" x3).
            (product as { review?: StructuralData.IReviewData | StructuralData.IReviewData[] }).review = dedupeReviews(
                data.review
            );
        }
    }

    /**
     * Whether a foreign product JSON-LD node describes the SAME product as our
     * managed Product schema (matched by name or sku). Used by the observer to
     * strip a duplicate of our own product while leaving unrelated third-party
     * Product blocks alone.
     *
     * Deliberately NOT gated on our schema already carrying reviews: Trustpilot
     * injects its Product block whether or not it holds reviews for the product,
     * and the duplicate has to go either way.
     */
    isCurrentProductNode(node: Record<string, unknown>): boolean {
        const product = this.findPrimaryProductElement();
        if (!product) {
            return false;
        }
        return (!!product.name && product.name === node['name']) || (!!product.sku && product.sku === node['sku']);
    }

    /**
     * Adds Magento reviews (via the base implementation) and additionally fetches
     * Trustpilot's product reviews + aggregateRating so they are present in the
     * server-rendered HTML, not only after the client-side widget runs.
     */
    override injectProductReviews(
        product: Product<IProductDataV3>,
        data: StructuralData.IProductData | StructuralData.IProductGroupData
    ): void {
        super.injectProductReviews(product, data);
        this.injectTrustpilotReviews(product, data);
    }

    private injectTrustpilotReviews(
        product: Product<IProductDataV3>,
        data: StructuralData.IProductData | StructuralData.IProductGroupData
    ): void {
        let skus: string | undefined;
        try {
            skus = product.getTrustpilotSkus();
        } catch {
            return;
        }
        const productName = product.data?.name;
        const url = data.url;
        if (!skus || !productName || !url) {
            return;
        }

        this.trustpilotReviewService
            .fetchProductReviews({ skus, productName, url })
            .then(result => {
                if (!result) {
                    return;
                }
                if (result.aggregateRating) {
                    data.aggregateRating = result.aggregateRating;
                }
                if (result.review?.length) {
                    (data as { review?: StructuralData.IReviewData | StructuralData.IReviewData[] }).review = result.review;
                }
                // Re-render so the merged schema is serialised into the SSR HTML.
                this.updateScripts();
            })
            .catch(() => undefined);
    }

    override getProductData(product: Product<IProductDataV3 | IProductDataV2>): StructuralData.IProductData {
        const data = super.getProductData(product);

        this.normaliseOffer(data);

        // `url`/`@id` must match the page canonical (also used for ItemList entries,
        // where the product's own url is the correct "canonical" for that fallback tier).
        const canonicalUrl = this.getCanonicalProductUrl(product);
        if (canonicalUrl) {
            data.url = canonicalUrl;
            data['@id'] = canonicalUrl;
        }

        this.setProductDescription(data, product);

        return data;
    }

    /**
     * Drops offers that would misrepresent the product to search engines, rather
     * than "fixing" them. A fabricated free/in-stock offer (e.g. price "0" for a
     * quote-only product, which the page itself renders as "Call us for pricing")
     * is a quality signal working against us. Do not re-introduce a synthetic
     * price here.
     *
     * A Product with no `offers` is still valid schema.org, but note it is NOT
     * eligible for a product-snippet rich result: Google requires at least one of
     * offers/aggregateRating/review. That is a deliberate trade — see
     * getListingData(), which keeps such products out of the category ItemList
     * rather than listing them ineligible.
     */
    private normaliseOffer(data: StructuralData.IProductData): void {
        const offers = data.offers;
        if (!offers) {
            return;
        }

        const price = Number(offers.price);
        if (!Number.isFinite(price) || price <= 0) {
            delete data.offers;
            return;
        }

        if (offers.seller && !offers.seller.name) {
            // getOrganizationData() falls back to '' when structuralData.name is
            // unset, and an empty seller name makes Google treat the field as
            // missing/invalid — omit it instead. app.config.ts now sets that name,
            // so this is a backstop for a store-scoped config that blanks it.
            delete offers.seller;
        }
    }

    /**
     * Resolves the canonical URL for a product using the same precedence Meta
     * uses to build <link rel="canonical"> (see Meta.updateModel/getMetaProperty),
     * so the structured data can never drift from the rendered canonical tag.
     * Falls back to the parent/family url (what a variant's own url canonicalises
     * to), then the product's own url. `product.parent` returns `this` when there
     * is no parent, and its `.data` may be undefined, hence the optional chaining.
     */
    private getCanonicalProductUrl(product: Product<IProductDataV3 | IProductDataV2>): string | undefined {
        const metaCanonical: unknown = this.meta.getMetaProperty('canonical', product);
        const metaUrl: unknown = this.meta.getMetaProperty('url', product);

        const parentUrl: unknown = product.parent?.data?.url;
        const productUrl: unknown = product.data?.url;

        const canonical: string | undefined =
            (typeof metaCanonical === 'string' && metaCanonical) ||
            (typeof metaUrl === 'string' && metaUrl) ||
            (typeof parentUrl === 'string' && parentUrl) ||
            (typeof productUrl === 'string' && productUrl) ||
            undefined;

        if (!canonical) {
            return undefined;
        }

        // Url.toUrl() returns its input unchanged whenever it already contains
        // '://' (see dragonfly-core Url.toUrl), so it's idempotent/safe to call
        // unconditionally here even though the canonical above is normally already
        // absolute — no need to special-case absolute vs. path-shaped input.
        return this.url.toUrl(canonical);
    }

    /**
     * The base service sources `description` only from `meta.description`, so
     * fall back through the other description-shaped fields, sanitising HTML out
     * with the same tag-strip idiom used in addFaqData.
     *
     * NOTE on category ItemList entries: this fallback is currently INERT for
     * them, and deliberately so. Listing products are loaded at verbosity 2
     * (IProductDataV2), and verbosity slicing happens at Mongo index time — the
     * verbosity-2 documents physically carry none of `meta`, `description` or a
     * real `short_description` (measured: `short_description` is non-empty on 2
     * of 21,758 verbosity-2 product docs, and those 2 are lorem-ipsum test
     * fixtures). Populating ItemList descriptions therefore needs the indexer to
     * emit `meta.description` at verbosity 2 — see ASR-291. This chain is kept so
     * that lands as a data-only change with no frontend follow-up, and it is
     * harmless meanwhile: an absent source simply leaves `description` unset.
     */
    private setProductDescription(data: StructuralData.IProductData, product: Product<IProductDataV3 | IProductDataV2>): void {
        if (data.description) {
            return;
        }

        const productData = product.data;
        const raw =
            (<IProductDataV3>productData)?.meta?.description ||
            (<IProductDataV3>productData)?.description ||
            (<IProductDataV2>productData)?.short_description;

        if (!raw) {
            return;
        }

        const description = raw
            .replace(/<[^>]*>?/gm, '')
            // Decode the common entities the source HTML carries, so a literal
            // "&amp;" never reaches the JSON-LD text. `&amp;` is decoded last so
            // an encoded "&amp;lt;" yields the text "&lt;" rather than "<".
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;|&apos;/g, "'")
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .split('\n')
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();

        if (description) {
            data.description = description;
        }
    }

    /**
     * Google requires at least one of offers/aggregateRating/review for a Product
     * to be eligible for a product-snippet rich result. Since normaliseOffer()
     * (correctly) drops the fabricated zero-price offer for quote-only products,
     * those products now have none of the three — so listing them inside the
     * category ItemList contributes ineligible noise to the one markup block that
     * earns most of this site's product-snippet traffic. Drop them from the list
     * instead, renumbering positions and numberOfItems to stay consistent.
     *
     * They lose nothing else: each remains crawlable via its on-page link and
     * keeps its own PDP Product schema, which still truthfully describes that
     * page's primary entity. Exclusion is also self-healing: a product re-enters
     * the list automatically on the next render as soon as it gains an offer, an
     * aggregateRating or a review — no code change or backfill is needed.
     *
     * When NOTHING qualifies (every product on the page is quote-only, or the
     * category is genuinely empty), the ItemList is omitted wholesale rather
     * than emitted as an empty node — see pruneHollowListing(), which is what
     * actually drops it before serialisation (this method's return type can't
     * itself be optional).
     */
    override getListingData(listing: ProductListing<DfProduct<IProductDataV2>>): StructuralData.IListingData {
        const data = super.getListingData(listing);
        const items = data.itemListElement || [];
        const qualifying = items.filter(entry => !!(entry.item?.offers || entry.item?.aggregateRating || entry.item?.review));

        if (qualifying.length === items.length) {
            return data;
        }

        // May produce a hollow (empty) itemListElement when qualifying.length is 0 —
        // that hollow node is dropped wholesale before serialisation by
        // pruneHollowListing() (called from updateScripts()), so no empty ItemList
        // is ever emitted to the page.
        data.itemListElement = qualifying.map((entry, index) => ({ ...entry, position: index + 1 }));
        data.numberOfItems = qualifying.length + '';

        return data;
    }

    override addModelData(model: Model): void {
        super.addModelData(model);

        const components = this.getComponents(model);
        if (components?.length) {
            const faqItems = this.getFaqItems(components);
            if (faqItems?.length) {
                this.addFaqData(faqItems);
            }
        }
    }

    private getComponents(model: Model): NICmsContentComponent.IData[] {
        switch (model.type) {
            case 'product':
                return this.getProductComponents(model as Product<IProductDataV3 | IProductDataV2>);
            case 'category':
                return this.getCategoryComponents(model as Category<ICategoryDataV3>);
            default:
                return [];
        }
    }

    private getCategoryComponents(category: Category<ICategoryDataV3>): NICmsContentComponent.IData[] {
        const block = this.iCmsBlockResource.retrieveExisting({
            bareUrl: NCategoryViewLayout.BLOCK_ID,
            variant: category.lpLayoutVariant
        }) as ICmsBlock<NCategoryViewLayout.IBlockData>;
        return [
            ...(block?.data?.content?.topComponents || []),
            ...(block?.data?.content?.middleComponents || []),
            ...(block?.data?.content?.bottomComponents || [])
        ];
    }

    private getProductComponents(product: Product<IProductDataV3 | IProductDataV2>): NICmsContentComponent.IData[] {
        const outletBlock = this.getProductOutletBlock(product);
        const pdpLayoutBlock = this.iCmsBlockResource.retrieveExisting({
            bareUrl: NProductViewLayout.BLOCK_ID,
            variant: product.ppLayoutVariant
        }) as ICmsBlock<NProductViewLayout.IBlockData>;
        return [...(outletBlock?.data?.content?.components || []), ...(pdpLayoutBlock?.data?.content?.bottomComponents || [])];
    }

    private getProductOutletBlock(
        product: Product<IProductDataV3 | IProductDataV2>
    ): ICmsBlock<NICmsOutletBlockComponent.IBlockData> | undefined {
        return this.iCmsBlockResource.retrieveExisting({
            bareUrl: NICmsOutletBlockComponent.BLOCK_ID,
            variant: product.pdpComponentsOutletVariant
        });
    }

    private addFaqData(faqItems: IFaqItem[]): void {
        if (faqItems?.length) {
            try {
                const faqPageData: StructuralData.IFAQPageData = {
                    '@type': StructuralData.Type.faqPage,
                    '@context': 'http://schema.org',
                    mainEntity: faqItems.map(faqItem => {
                        return {
                            '@type': StructuralData.Type.question,
                            name: faqItem.question
                                .replace(/<[^>]*>?/gm, '')
                                .split('\n')
                                .join(' '),
                            acceptedAnswer: {
                                '@type': StructuralData.Type.answer,
                                text: faqItem.answer
                                    .replace(/<[^>]*>?/gm, '')
                                    .split('\n')
                                    .join(' ')
                            }
                        };
                    })
                };
                this.add(faqPageData);
            } catch (e) {}
        }
    }

    private getFaqItems(components: NICmsContentComponent.IData[]): IFaqItem[] {
        const items: (IFaqItem | undefined)[] = flatten(
            components.map(i => {
                const id = i?.id;
                if (id === NCmsInformationFaq.componentId) {
                    const informationFaqData = i as NCmsInformationFaq.IComponentData;
                    const items = informationFaqData?.items || [];
                    return items.map(item => {
                        const question = item?.title;
                        if (question) {
                            const textComponents = (item?.components || []).filter(
                                i => i?.id === NCmsText.componentId
                            ) as NCmsText.IComponentData[];
                            const answer = textComponents
                                .map(i => i?.text)
                                ?.filter(i => !!i)
                                .join(' ');
                            return {
                                question,
                                answer
                            };
                        }
                        return undefined;
                    });
                }
                if (id === NCmsFaqComponent.ID) {
                    const cmsFaqData = i as NCmsFaqComponent.IData;
                    const items = cmsFaqData?.items || [];
                    return items
                        .map(item => {
                            const question = item?.question;
                            const answer = item?.answer;
                            if (question && answer) {
                                return {
                                    question,
                                    answer
                                };
                            }
                        })
                        .filter(i => !!i);
                }
                return undefined;
            })
        );
        return (items?.filter(i => !!(i?.answer && i?.question)) || []) as IFaqItem[];
    }
}
